/** @jsxImportSource @emotion/react */

import { ReactComponent as BrandSVG } from "assets/icons/brand.svg";
import HelmetCustom from "components/ui/Helmet";
import { dataAffiliate } from "constants/dataAffiliatePage";
import { useAppDispatch } from "hooks/app-hook";
import { useTranslation } from "react-i18next";
import { setOpenModal } from "store/modal";
import { ModalTypeValue } from "types/modal";

import { AffiliatePageStyle } from "./index.style";

const AffiliatePage = () => {
  const { t } = useTranslation(["affiliate", "common", "levelAff"]);
  const dispatch = useAppDispatch();

  const totalDeposit = dataAffiliate.reduce(
    (total, item) => total + Number(item.deposit),
    // (total, item) => 0,
    0
  );

  const totalCashout = dataAffiliate.reduce(
    (total, item) => total + Number(item.cashout),
    0
  );

  const totalWinLoss = dataAffiliate.reduce(
    (total, item) => total + Number(item.winLoss),
    0
  );

  const totalPlatformFee = dataAffiliate.reduce(
    (total, item) => total + Number(item.platformFee),
    // (total, item) => 5130,
    0
  );
  const totalBonus = dataAffiliate.reduce(
    (total, item) => total + Number(item.bonus),
    0
  );
  const totalPaymentFee = dataAffiliate.reduce(
    (total, item) => total + Number(item.paymentFee),
    0
  );

  const totalComm = dataAffiliate.reduce(
    (total, item) => total + Number(item.commission),
    0
  );

  function formatNumberWithCommas(number: string | number) {
    const intNum = typeof number === "string" ? Number(number) : number;
    return intNum.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  return (
    <div css={AffiliatePageStyle.section}>
      <HelmetCustom title={t("common:Affiliate")} />
      <div css={AffiliatePageStyle.wrapper}>
        <h1 className="title">{t("title")}</h1>
        <div css={AffiliatePageStyle.wrapInfo}>
          <div className="row-info">
            <div className="wrap-item">
              <h4 className="subtitle">{t("PaymentFee_title")}:</h4>
              <p className="desc">{t("PaymentFee_text")}</p>
              <p className="desc">+</p>
              <p className="desc">{t("PaymentFee_text1")}</p>
            </div>
            <div className="wrap-item">
              <h4 className="subtitle">{t("PlatformFeeTitle")}:</h4>
              <p className="desc">{t("PlatformFeeText")}</p>
            </div>
          </div>
          <div className="wrap-item">
            <h4 className="subtitle">{t("Bonus_title")}:</h4>
            <p className="desc">
              {t("Bonus_text1")} + {t("Bonus_text2")}
            </p>
          </div>
          <div className="wrap-item">
            <h4 className="subtitle">{t("NetProfit_title")}</h4>
            <p className="desc">{t("NetProfit_text")}</p>
          </div>
        </div>
        <div css={AffiliatePageStyle.wrapBtnReg}>
          <button
            className="btnRegisterAff"
            onClick={() => dispatch(setOpenModal(ModalTypeValue.Affiliate))}
          >
            <BrandSVG />
            {t("AffReg")}
          </button>
          <a
            href="https://ed.pos8.co"
            target="_blank"
            className="href-link"
            rel="noreferrer"
          >
            {t("AffLogin")}
          </a>
        </div>
        <div css={AffiliatePageStyle.wrapTable}>
          <h2 className="title">{t("tablet_title")}</h2>
          <div className="table-inner">
            <table>
              <thead>
                <tr>
                  <th>{t("thead_1")}</th>
                  <th>{t("thead_2")}</th>
                  <th>{t("thead_3")}</th>
                  <th>{t("thead_4")}</th>
                  <th>{t("thead_5")}</th>
                  <th>{t("thead_6")}</th>
                  <th>{t("thead_7")}</th>
                  <th>{t("thead_8")}</th>
                </tr>
              </thead>
              <tbody>
                {dataAffiliate.map((item, index) => (
                  <tr key={index}>
                    <td>{t(item.player)}</td>
                    <td className={`${Number(item.deposit) < 0 ? "down" : ""}`}>
                      {formatNumberWithCommas(item.deposit) + ".00"}
                    </td>
                    <td className={`${Number(item.cashout) < 0 ? "down" : ""}`}>
                      {formatNumberWithCommas(item.cashout)}
                    </td>
                    <td className={`${Number(item.winLoss) < 0 ? "down" : ""}`}>
                      {formatNumberWithCommas(item.winLoss) + ".00"}
                    </td>
                    <td
                      className={`${
                        Number(item.platformFee) < 0 ? "down" : ""
                      }`}
                    >
                      {formatNumberWithCommas(item.platformFee)}
                    </td>
                    <td className={`${Number(item.bonus) < 0 ? "down" : ""}`}>
                      {formatNumberWithCommas(item.bonus)}
                    </td>
                    <td
                      className={`${Number(item.paymentFee) < 0 ? "down" : ""}`}
                    >
                      {formatNumberWithCommas(item.paymentFee)}
                    </td>
                    <td
                      className={`${Number(item.commission) < 0 ? "down" : ""}`}
                    >
                      {formatNumberWithCommas(item.commission)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th>{t("table_total")}</th>
                  <th>{formatNumberWithCommas(totalDeposit) + ".00"}</th>
                  <th>{formatNumberWithCommas(totalCashout)}</th>
                  <th>{formatNumberWithCommas(totalWinLoss) + ".00"}</th>
                  <th>{formatNumberWithCommas(totalPlatformFee)}</th>
                  <th>{formatNumberWithCommas(totalBonus)}</th>
                  <th>{formatNumberWithCommas(totalPaymentFee)}</th>
                  <th>{formatNumberWithCommas(totalComm)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="group-notice">
            <div className="notice-title">{t("noteTitle")}:</div>
            <p>- {t("noteItem_1")}</p>
            <p>- {t("noteItem_2")}</p>
            <p>- {t("noteItem_3")}</p>
          </div>
        </div>
        <div css={AffiliatePageStyle.wrapTable}>
          <h2 className="title">{t("levelAff:title")}</h2>
          <div className="table-inner">
            <table>
              <thead>
                <tr>
                  <th>{t("levelAff:thead_1")}</th>
                  <th>{t("levelAff:thead_5")}</th>
                  <th>{t("levelAff:thead_3")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>
                    {"\u2265"} {"1"}
                  </td>
                  <td>30%</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>
                    {"\u2265"} {"5"}
                  </td>
                  <td>40%</td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>
                    {"\u2265"} {"10"}
                  </td>
                  <td>50%</td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* <div className="note">* {t("levelAff:noteBottom")}</div> */}
          <div className="note">* {t("levelAff:bonus_note_1")}</div>
          <div className="note">* {t("levelAff:bonus_note_2")}</div>
        </div>
        <div css={AffiliatePageStyle.wrapT_C}>
          <h2 className="title">{t("affiliate:howjoinAff:title")}</h2>
          <div className="wrap-term">
            <p>{t("affiliate:howjoinAff:desc_1")}</p>
            <p>{t("affiliate:howjoinAff:desc_2")}</p>
            <p>{t("affiliate:howjoinAff:desc_3")}</p>
          </div>
        </div>
        <div css={AffiliatePageStyle.wrapT_C}>
          <h2 className="title">{t("affiliate:commisRelease:title")}</h2>
          <div className="wrap-term">
            <p>{t("affiliate:commisRelease:desc")}</p>
          </div>
        </div>
        <div css={AffiliatePageStyle.wrapT_C}>
          <h2 className="title">{t("affiliate:operating:title")}</h2>
          <div className="wrap-term">
            <p>{t("affiliate:operating:desc_1")}</p>
            <p>{t("affiliate:operating:desc_2")}</p>
            <p>{t("affiliate:operating:desc_3")}</p>
          </div>
        </div>
        <div css={AffiliatePageStyle.wrapT_C}>
          <h2 className="title">{t("affiliate:netProfit:title")}</h2>
          <div className="wrap-term">
            <p>{t("affiliate:netProfit:desc_1")}</p>
          </div>
        </div>
        <div css={AffiliatePageStyle.wrapT_C}>
          <h2 className="title">{t("affiliate:additional:title")}</h2>
          <div className="wrap-term">
            <p>{t("affiliate:additional:desc_1")}</p>
            <p>{t("affiliate:additional:desc_2")}</p>
          </div>
        </div>
        <div css={AffiliatePageStyle.wrapT_C}>
          <h2 className="title">{t("affiliate:termCond:title")}</h2>
          <div className="wrap-term">
            <p>{t("affiliate:termCond:desc_1")}</p>
            <p>{t("affiliate:termCond:desc_2")}</p>
            <p>{t("affiliate:termCond:desc_3")}</p>
            <p>{t("affiliate:termCond:desc_4")}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default AffiliatePage;
